import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getDataAPI, putDataAPI, deleteDataAPI } from "../../utils/fetchData";
import LayoutHeader from "../../layout/LayoutHeader";
import Modal from "../../components/Modal";
import { useModal } from "../../hooks/useModal";

const EditCatalog = () => {
    const { catalogId } = useParams();
    const [editData, setEditData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [masterContributors, setMasterContributors] = useState([
        { name: '', role: 'Writer', percentage: '', advanceFee: '' }
    ]);
    const [publishingContributors, setPublishingContributors] = useState([
        { name: '', role: 'Writer', percentage: '', pro: '', ipiNumber: '', publishingLine: '' }
    ]);
    const token = localStorage.getItem('dizeeToken');
    const navigate = useNavigate();
    const { modalConfig, showModal, closeModal } = useModal();

    useEffect(() => {
        loadCatalogData();
    }, [catalogId]);

    const loadCatalogData = async () => {
        try {
            const token = JSON.parse(localStorage.getItem('dizeeToken'));
            const response = await getDataAPI('user/getCatalog', token);
            
            if (response.data.status === 200 && response.data.data) {
                const catalogData = Array.isArray(response.data.data) ? response.data.data : response.data.data.data;
                const catalogItem = catalogData.find(item => item._id === catalogId);
                
                if (catalogItem) {
                    setEditData(catalogItem);
                    
                    // Load master contributors if they exist
                    if (catalogItem.master_contributors && catalogItem.master_contributors.length > 0) {
                        setMasterContributors(catalogItem.master_contributors.map(c => ({
                            name: c.name || '',
                            role: c.role || 'Writer',
                            percentage: c.percentage || '',
                            advanceFee: c.advance_fee || ''
                        })));
                    }
                    
                    // Load publishing contributors if they exist
                    if (catalogItem.publishing_contributors && catalogItem.publishing_contributors.length > 0) {
                        setPublishingContributors(catalogItem.publishing_contributors.map(c => ({
                            name: c.name || '',
                            role: c.role || 'Writer',
                            percentage: c.percentage || '',
                            pro: c.pro || '',
                            ipiNumber: c.ipi_number || '',
                            publishingLine: c.publishing_line || ''
                        })));
                    }
                } else {
                    showModal('Not Found', 'Catalog item not found', 'error');
                    navigate('/catalog/view-all');
                }
            }
        } catch (error) {
            console.error('Error loading catalog item:', error);
            showModal('Load Error', 'Error loading catalog data', 'error');
        } finally {
            setIsLoadingData(false);
        }
    };

    // Master Contributors Functions
    const addMasterContributor = () => {
        setMasterContributors(prev => [...prev, { name: '', role: 'Writer', percentage: '', advanceFee: '' }]);
    };

    const removeMasterContributor = (index) => {
        if (masterContributors.length > 1) {
            setMasterContributors(prev => prev.filter((_, i) => i !== index));
        }
    };

    const updateMasterContributor = (index, key, value) => {
        setMasterContributors(prev => prev.map((contributor, i) => i === index ? { ...contributor, [key]: value } : contributor));
    };

    const getMasterPercentageTotal = () => {
        return masterContributors.reduce((total, contributor) => {
            const percentage = parseFloat(contributor.percentage) || 0;
            return total + percentage;
        }, 0);
    };

    const isMasterPercentageValid = () => getMasterPercentageTotal() === 100;

    // Publishing Contributors Functions
    const addPublishingContributor = () => {
        setPublishingContributors(prev => [...prev, { name: '', role: 'Writer', percentage: '', pro: '', ipiNumber: '', publishingLine: '' }]);
    };

    const removePublishingContributor = (index) => {
        if (publishingContributors.length > 1) {
            setPublishingContributors(prev => prev.filter((_, i) => i !== index));
        }
    };

    const updatePublishingContributor = (index, key, value) => {
        setPublishingContributors(prev => prev.map((contributor, i) => i === index ? { ...contributor, [key]: value } : contributor));
    };

    const getPublishingPercentageTotal = () => {
        return publishingContributors.reduce((total, contributor) => {
            const percentage = parseFloat(contributor.percentage) || 0;
            return total + percentage;
        }, 0);
    };

    const isPublishingPercentageValid = () => getPublishingPercentageTotal() === 100;

    const saveData = async () => {
        setIsLoading(true);
        try {
            // Prepare updated data with contributors
            const updatedData = {
                ...editData,
                master_contributors: masterContributors
                    .filter(contributor => contributor.name && contributor.name.trim() !== '')
                    .map(contributor => ({
                        name: contributor.name?.trim() || '',
                        role: contributor.role || '',
                        percentage: parseFloat(contributor.percentage) || 0,
                        advance_fee: parseFloat(contributor.advanceFee) || 0
                    })),
                publishing_contributors: publishingContributors
                    .filter(contributor => contributor.name && contributor.name.trim() !== '')
                    .map(contributor => ({
                        name: contributor.name?.trim() || '',
                        role: contributor.role || '',
                        percentage: parseFloat(contributor.percentage) || 0,
                        pro: contributor.pro || '',
                        ipi_number: contributor.ipiNumber || '',
                        publishing_line: contributor.publishingLine || ''
                    }))
            };

            let response = await putDataAPI('user/updateCatalog', {
                catalogId: catalogId,
                data: updatedData
            }, token);
            console.log(`response::`, response.data);
            
            // Trigger reminder system update after successful save
            if (response.data.status === 200) {
                console.log('📅 Catalog updated - triggering reminder system check...');
                
                // Dispatch storage event to trigger reminder updates
                localStorage.setItem('catalog_updated', Date.now().toString());
                
                // Also dispatch custom event for same-tab updates
                window.dispatchEvent(new CustomEvent('localStorageUpdate', {
                    detail: {
                        key: 'catalog_updated',
                        newValue: Date.now().toString()
                    }
                }));
                
                // Force reminder system to check for updates
                setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('checkOverdueReminders'));
                }, 500);
            }
            
            setTimeout(() => {
                navigate('/catalog/view-all');
            }, 1500);
        } catch (error) {
            console.error('Error updating catalog item:', error);
            showModal('Update Error', 'Error updating catalog item. Please try again.', 'error');
        }
        setIsLoading(false);
    }

    const deleteCatalogItem = async () => {
        setIsDeleting(true);
        try {
            let response = await deleteDataAPI(`user/deleteCatalog/${catalogId}`, token);
            console.log(`delete response::`, response.data);
            
            if (response.data.status === 200) {
                // Delete associated reminders after successful catalog deletion
                const songName = editData.song_title || editData.track_name || 'Unknown Song';
                console.log(`🗑️ Deleting reminders for deleted catalog item: ${songName}`);
                
                // Import the reminders manager if available
                if (window.deleteRemindersForRelease) {
                    window.deleteRemindersForRelease(catalogId, songName);
                }
                
                // Trigger storage events to update reminder app
                localStorage.setItem('catalog_updated', Date.now().toString());
                window.dispatchEvent(new CustomEvent('localStorageUpdate', {
                    detail: {
                        key: 'catalog_updated',
                        newValue: Date.now().toString()
                    }
                }));
                
                showModal('Success', 'Catalog item and associated reminders deleted successfully!', 'success');
            } else {
                showModal('Success', 'Catalog item deleted successfully!', 'success');
            }
            
            setTimeout(() => {
                navigate('/catalog/view-all');
            }, 1500);
        } catch (error) {
            console.error('Error deleting catalog item:', error);
            showModal('Delete Error', 'Error deleting catalog item. Please try again.', 'error');
        }
        setIsDeleting(false);
        setShowDeleteConfirm(false);
    }

    const basicFields = [
        { key: 'song_title', label: 'Song Title', placeholder: 'Enter song title' },
        { key: 'artist_name', label: 'Artist Name', placeholder: 'Enter artist name' },
        { key: 'album_name', label: 'Album Name', placeholder: 'Enter album name' },
        { key: 'genre', label: 'Genre', placeholder: 'Enter genre' },
        { key: 'release_date', label: 'Release Date', placeholder: 'Enter release date', type: 'date' },
        { key: 'duration', label: 'Duration', placeholder: 'Enter duration (e.g., 3:45)' },
        { key: 'isrc', label: 'ISRC', placeholder: 'Enter ISRC code' },
        { key: 'upc', label: 'UPC', placeholder: 'Enter UPC code' },
        { key: 'record_label', label: 'Record Label', placeholder: 'Enter record label' },
        { key: 'distributor', label: 'Distributor', placeholder: 'Enter distributor' },
        { key: 'songwriter', label: 'Songwriter', placeholder: 'Enter songwriter(s)' },
        { key: 'producer', label: 'Producer', placeholder: 'Enter producer(s)' },
        { key: 'notes', label: 'Notes', placeholder: 'Enter additional notes', type: 'textarea' }
    ];

    const advancedFields = [
        { key: 'status', label: 'Status', placeholder: 'Select status', type: 'select', options: ['released', 'unreleased', 'archived'] },
        { key: 'album_artwork', label: 'Album Artwork URL', placeholder: 'Enter artwork URL' },
        { key: 'featured_artists', label: 'Featured Artists', placeholder: 'Enter featured artists' },
        { key: 'track_number', label: 'Track Number', placeholder: 'Enter track number', type: 'number' },
        { key: 'disc_number', label: 'Disc Number', placeholder: 'Enter disc number', type: 'number' },
        { key: 'bpm', label: 'BPM', placeholder: 'Enter BPM', type: 'number' },
        { key: 'key_signature', label: 'Key Signature', placeholder: 'Enter key signature' },
        { key: 'language', label: 'Language', placeholder: 'Enter language' },
        { key: 'explicit', label: 'Explicit Content', type: 'checkbox' },
        { key: 'copyright', label: 'Copyright', placeholder: 'Enter copyright information' },
        { key: 'publishing', label: 'Publishing', placeholder: 'Enter publishing information' },
        { key: 'mechanical_rights', label: 'Mechanical Rights', placeholder: 'Enter mechanical rights info' },
        { key: 'sync_rights', label: 'Sync Rights', placeholder: 'Enter sync rights info' },
        { key: 'master_rights', label: 'Master Rights', placeholder: 'Enter master rights info' },
        { key: 'streaming_platforms', label: 'Streaming Platforms', placeholder: 'Enter available platforms', type: 'textarea' },
        { key: 'metadata_tags', label: 'Metadata Tags', placeholder: 'Enter metadata tags', type: 'textarea' },
        { key: 'site_url', label: 'SongStats URL', placeholder: 'Enter SongStats site URL' },
        { key: 'spotify_track_id', label: 'Spotify Track ID', placeholder: 'Enter Spotify track ID' },
        { key: 'songstats_track_id', label: 'SongStats Track ID', placeholder: 'Enter SongStats track ID' }
    ];

    const renderField = (field) => {
        const fieldClass = "bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all";
        
        return (
            <div key={field.key} className={fieldClass}>
                <label className="block text-white text-[12px] font-semibold mb-2">
                    {field.label}
                </label>
                {field.type === 'textarea' ? (
                    <textarea
                        value={editData[field.key] || ''}
                        onChange={(e) => setEditData({
                            ...editData,
                            [field.key]: e.target.value
                        })}
                        placeholder={field.placeholder}
                        className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] resize-none h-20"
                    />
                ) : field.type === 'select' ? (
                    <select
                        value={editData[field.key] || ''}
                        onChange={(e) => setEditData({
                            ...editData,
                            [field.key]: e.target.value
                        })}
                        className="w-full bg-[#1A1A1A] border-none outline-none text-white text-[12px] rounded-md p-2"
                    >
                        <option value="">{field.placeholder}</option>
                        {field.options?.map(option => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                ) : field.type === 'checkbox' ? (
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            checked={editData[field.key] || false}
                            onChange={(e) => setEditData({
                                ...editData,
                                [field.key]: e.target.checked
                            })}
                            className="mr-2 w-4 h-4 text-[#17A2B8] bg-transparent border border-[rgba(255,255,255,0.3)] rounded focus:ring-[#17A2B8] focus:ring-2"
                        />
                        <span className="text-[12px] text-[rgba(255,255,255,0.7)]">Mark as explicit content</span>
                    </div>
                ) : (
                    <input
                        type={field.type || 'text'}
                        value={editData[field.key] || ''}
                        onChange={(e) => setEditData({
                            ...editData,
                            [field.key]: e.target.value
                        })}
                        placeholder={field.placeholder}
                        className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                    />
                )}
            </div>
        );
    };

    if (isLoadingData) {
        return (
            <LayoutHeader>
                <div className="flex justify-center w-full">
                    <div className="w-[390px] h-[80vh] flex flex-col items-center">
                        <div className="text-white text-left py-[50px]">Loading catalog data...</div>
                    </div>
                </div>
            </LayoutHeader>
        );
    }

    return (
        <LayoutHeader>
            <div className="flex justify-center w-full">
                <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
                    
                    {/* Header */}
                    <div className="px-4 my-[50px] flex w-full justify-between">
                        <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Edit Catalog Item</span>
                        <span className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" onClick={() => navigate(-1)}>Go back</span>
                    </div>

                    <div className="w-full px-4 flex-1 overflow-y-auto">
                        <div className="flex flex-col">
                            <div className="mb-6">
                                <span className="text-[14px] text-white" style={{ fontWeight: 300 }}>Edit catalog details</span>
                                <p className="text-[10px] text-[rgba(255,255,255,0.7)] mt-1">Update the information for this catalog entry</p>
                            </div>

                            {/* Basic Fields */}
                            <div className="space-y-4 mb-6">
                                {basicFields.map(field => renderField(field))}
                            </div>

                            {/* Master Contributors Section */}
                            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all mb-4">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <label className="block text-white text-[12px] font-semibold">Master Contributors</label>
                                        <span className={`text-[10px] px-2 py-1 rounded ${
                                            isMasterPercentageValid() 
                                                ? 'bg-green-500/20 text-green-400' 
                                                : 'bg-red-500/20 text-red-400'
                                        }`}>
                                            {getMasterPercentageTotal()}%
                                        </span>
                                    </div>
                                    <button
                                        onClick={addMasterContributor}
                                        className="text-[#17A2B8] text-[10px] hover:opacity-70 flex items-center gap-1"
                                    >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                        Add
                                    </button>
                                </div>
                                {masterContributors.map((contributor, index) => (
                                    <div key={index} className="space-y-2 mb-4 last:mb-0 p-3 bg-[rgba(255,255,255,0.02)] rounded-lg border border-[rgba(255,255,255,0.05)]">
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="text"
                                                value={contributor.name}
                                                onChange={(e) => updateMasterContributor(index, 'name', e.target.value)}
                                                className="flex-1 bg-transparent border border-[rgba(255,255,255,0.1)] rounded px-2 py-1 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                                placeholder={`Master Contributor ${index + 1}`}
                                            />
                                            {masterContributors.length > 1 && (
                                                <button
                                                    onClick={() => removeMasterContributor(index)}
                                                    className="text-red-400 hover:text-red-300 text-[10px] p-1"
                                                >
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <select
                                                value={contributor.role}
                                                onChange={(e) => updateMasterContributor(index, 'role', e.target.value)}
                                                className="flex-1 bg-transparent border border-[rgba(255,255,255,0.1)] rounded px-2 py-1 text-white text-[12px] appearance-none cursor-pointer"
                                                style={{ backgroundImage: 'none' }}
                                            >
                                                <option value="Writer" className="bg-black text-white">Writer</option>
                                                <option value="Producer" className="bg-black text-white">Producer</option>
                                                <option value="Engineer" className="bg-black text-white">Engineer</option>
                                                <option value="Mixer" className="bg-black text-white">Mixer</option>
                                                <option value="Drummer" className="bg-black text-white">Drummer</option>
                                                <option value="Guitarist" className="bg-black text-white">Guitarist</option>
                                                <option value="Bassist" className="bg-black text-white">Bassist</option>
                                                <option value="Vocalist" className="bg-black text-white">Vocalist</option>
                                                <option value="Other" className="bg-black text-white">Other</option>
                                            </select>
                                            <input
                                                type="text"
                                                value={contributor.percentage}
                                                onChange={(e) => updateMasterContributor(index, 'percentage', e.target.value)}
                                                className={`w-16 bg-transparent border rounded px-2 py-1 text-[12px] placeholder-[rgba(255,255,255,0.5)] ${
                                                    isMasterPercentageValid() 
                                                        ? 'border-[rgba(255,255,255,0.1)] text-white' 
                                                        : 'border-red-500 text-red-400'
                                                }`}
                                                placeholder="%"
                                                onKeyPress={(e) => {
                                                    if (!/[0-9.]/.test(e.key)) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                            />
                                            <input
                                                type="text"
                                                value={contributor.advanceFee}
                                                onChange={(e) => updateMasterContributor(index, 'advanceFee', e.target.value)}
                                                className="flex-1 bg-transparent border border-[rgba(255,255,255,0.1)] rounded px-2 py-1 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                                placeholder="Advance/Fee"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Publishing Contributors Section */}
                            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all mb-6">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <label className="block text-white text-[12px] font-semibold">Publishing Contributors</label>
                                        <span className={`text-[10px] px-2 py-1 rounded ${
                                            isPublishingPercentageValid() 
                                                ? 'bg-green-500/20 text-green-400' 
                                                : 'bg-red-500/20 text-red-400'
                                        }`}>
                                            {getPublishingPercentageTotal()}%
                                        </span>
                                    </div>
                                    <button
                                        onClick={addPublishingContributor}
                                        className="text-[#17A2B8] text-[10px] hover:opacity-70 flex items-center gap-1"
                                    >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                        Add
                                    </button>
                                </div>
                                {publishingContributors.map((contributor, index) => (
                                    <div key={index} className="space-y-2 mb-4 last:mb-0 p-3 bg-[rgba(255,255,255,0.02)] rounded-lg border border-[rgba(255,255,255,0.05)]">
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="text"
                                                value={contributor.name}
                                                onChange={(e) => updatePublishingContributor(index, 'name', e.target.value)}
                                                className="flex-1 bg-transparent border border-[rgba(255,255,255,0.1)] rounded px-2 py-1 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                                placeholder={`Publishing Contributor ${index + 1}`}
                                            />
                                            {publishingContributors.length > 1 && (
                                                <button
                                                    onClick={() => removePublishingContributor(index)}
                                                    className="text-red-400 hover:text-red-300 text-[10px] p-1"
                                                >
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <select
                                                value={contributor.role}
                                                onChange={(e) => updatePublishingContributor(index, 'role', e.target.value)}
                                                className="flex-1 bg-transparent border border-[rgba(255,255,255,0.1)] rounded px-2 py-1 text-white text-[12px] appearance-none cursor-pointer"
                                                style={{ backgroundImage: 'none' }}
                                            >
                                                <option value="Writer" className="bg-black text-white">Writer</option>
                                                <option value="Producer" className="bg-black text-white">Producer</option>
                                                <option value="Engineer" className="bg-black text-white">Engineer</option>
                                                <option value="Mixer" className="bg-black text-white">Mixer</option>
                                                <option value="Drummer" className="bg-black text-white">Drummer</option>
                                                <option value="Guitarist" className="bg-black text-white">Guitarist</option>
                                                <option value="Bassist" className="bg-black text-white">Bassist</option>
                                                <option value="Vocalist" className="bg-black text-white">Vocalist</option>
                                                <option value="Other" className="bg-black text-white">Other</option>
                                            </select>
                                            <input
                                                type="text"
                                                value={contributor.percentage}
                                                onChange={(e) => updatePublishingContributor(index, 'percentage', e.target.value)}
                                                className={`w-16 bg-transparent border rounded px-2 py-1 text-[12px] placeholder-[rgba(255,255,255,0.5)] ${
                                                    isPublishingPercentageValid() 
                                                        ? 'border-[rgba(255,255,255,0.1)] text-white' 
                                                        : 'border-red-500 text-red-400'
                                                }`}
                                                placeholder="%"
                                                onKeyPress={(e) => {
                                                    if (!/[0-9.]/.test(e.key)) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <select
                                                value={contributor.pro}
                                                onChange={(e) => updatePublishingContributor(index, 'pro', e.target.value)}
                                                className="flex-1 bg-transparent border border-[rgba(255,255,255,0.1)] rounded px-2 py-1 text-white text-[12px] appearance-none cursor-pointer"
                                                style={{ backgroundImage: 'none' }}
                                            >
                                                <option value="" className="bg-black text-white">PRO</option>
                                                <option value="ASCAP" className="bg-black text-white">ASCAP</option>
                                                <option value="BMI" className="bg-black text-white">BMI</option>
                                                <option value="SESAC" className="bg-black text-white">SESAC</option>
                                                <option value="SOCAN" className="bg-black text-white">SOCAN</option>
                                                <option value="PRS" className="bg-black text-white">PRS</option>
                                                <option value="GEMA" className="bg-black text-white">GEMA</option>
                                                <option value="SACEM" className="bg-black text-white">SACEM</option>
                                                <option value="Other" className="bg-black text-white">Other</option>
                                            </select>
                                            <input
                                                type="text"
                                                value={contributor.ipiNumber}
                                                onChange={(e) => updatePublishingContributor(index, 'ipiNumber', e.target.value)}
                                                className="flex-1 bg-transparent border border-[rgba(255,255,255,0.1)] rounded px-2 py-1 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                                placeholder="IPI #"
                                            />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="text"
                                                value={contributor.publishingLine}
                                                onChange={(e) => updatePublishingContributor(index, 'publishingLine', e.target.value)}
                                                className="flex-1 bg-transparent border border-[rgba(255,255,255,0.1)] rounded px-2 py-1 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                                placeholder="Publishing Line"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            {/* Advanced Section Toggle */}
                            <div className="mb-4">
                                <button
                                    onClick={() => setShowAdvanced(!showAdvanced)}
                                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all flex items-center justify-between"
                                >
                                    <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Advanced Fields</span>
                                    <svg 
                                        className={`w-4 h-4 text-white transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                                        fill="none" 
                                        stroke="currentColor" 
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                            </div>
                            
                            {/* Advanced Fields */}
                            {showAdvanced && (
                                <div className="space-y-4 mb-6">
                                    <div className="text-[10px] text-[rgba(255,255,255,0.7)] mb-4">
                                        Advanced catalog details for comprehensive tracking and AI integration
                                    </div>
                                    {advancedFields.map(field => renderField(field))}
                                </div>
                            )}

                            {/* Save Button */}
                            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all">
                                <button
                                    onClick={saveData}
                                    disabled={isLoading}
                                    className="w-full bg-[#17A2B8] text-white text-[12px] font-semibold py-3 rounded-lg hover:bg-[rgba(23,162,184,0.9)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                >
                                    {isLoading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                            Updating...
                                        </>
                                    ) : (
                                        'Update Catalog Item'
                                    )}
                                </button>
                            </div>

                            {/* Delete Button */}
                            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF2300] hover:bg-[rgba(255,35,0,0.05)] transition-all mt-4">
                                <button
                                    onClick={() => setShowDeleteConfirm(true)}
                                    disabled={isLoading || isDeleting}
                                    className="w-full bg-[#FF2300] text-white text-[12px] font-semibold py-3 rounded-lg hover:bg-[rgba(255,35,0,0.9)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                >
                                    Delete Catalog Item
                                </button>
                            </div>
                        </div>

                        {/* Help Text */}
                        <div className="mt-6 mb-4">
                            <div className="text-[12px] text-[rgba(255,255,255,0.5)] leading-[1.5] max-w-full">
                                <p>Update any fields as needed including splits information.</p>
                                <p>Advanced fields and splits help provide comprehensive data for AI assistance.</p>
                                <p>Changes will be saved and available to your AI assistant.</p>
                            </div>
                        </div>
                    </div>

                    {/* Delete Confirmation Modal */}
                    {showDeleteConfirm && (
                        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50">
                            <div className="w-full max-w-[350px] mx-4 transform overflow-hidden rounded-2xl bg-black border-2 border-[#FF2300] p-6 text-left align-middle shadow-xl transition-all">
                                {/* Header */}
                                <div className="flex items-center justify-between mb-6 relative">
                                    <h3 className="text-white text-[18px] font-bold text-left">Delete Catalog Item</h3>
                                    <button
                                        onClick={() => setShowDeleteConfirm(false)}
                                        className="text-gray-400 hover:text-white transition-colors"
                                        disabled={isDeleting}
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                {/* Content */}
                                <div className="mb-8">
                                    <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] text-left">
                                        Are you sure you want to delete this catalog item? This action cannot be undone.
                                    </p>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex space-x-4">
                                    <button
                                        onClick={deleteCatalogItem}
                                        disabled={isDeleting}
                                        className="flex-1 bg-[#FF2300] text-white text-[12px] font-semibold py-3 px-4 rounded-lg hover:bg-[#FF2300]/90 transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF2300] focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                    >
                                        {isDeleting ? (
                                            <>
                                                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-1"></div>
                                                Deleting...
                                            </>
                                        ) : (
                                            'Delete'
                                        )}
                                    </button>
                                    <button
                                        onClick={() => setShowDeleteConfirm(false)}
                                        disabled={isDeleting}
                                        className="flex-1 bg-transparent border-2 border-[#FF2300] text-white text-[12px] font-semibold py-3 px-4 rounded-lg hover:bg-[#FF2300]/10 transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF2300] focus:ring-opacity-50 disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            
            <Modal
                isOpen={modalConfig.isOpen}
                onClose={closeModal}
                onConfirm={modalConfig.onConfirm}
                title={modalConfig.title}
                message={modalConfig.message}
                type={modalConfig.type}
                showCancel={modalConfig.showCancel}
                confirmText={modalConfig.confirmText}
                cancelText={modalConfig.cancelText}
            />
        </LayoutHeader>
    );
};

export default EditCatalog;
